import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { queryKeys, useGetPdpQuery } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { ViewMode } from '@vizit/products';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import { PagesProvider } from '../../../pages';
import { ProductDetailsPage } from '../../../pages/components';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../templates';

type ProductDetailsSearch = {
  products?: 'open' | 'collapsed';
  tab?: 'hero' | 'carousel';
  carouselTab?: 'imported' | 'spark';
  heroTab?: 'imported' | 'spark';
  view?: ViewMode;
};

export const Route = createFileRoute('/_authenticated/library/products/$pdpId')({
  staticData: {
    pageName: 'Product Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  loader: async ({ location, params, context }) => {
    const organizationId = context.auth?.organizationId;
    const portfolioId = context.auth?.defaultPortfolioId;

    let productName = 'Product Details';

    if (organizationId && portfolioId) {
      try {
        const pdpData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.products.detail({
            organization_id: organizationId,
            portfolio_id: portfolioId,
            pdp_id: params.pdpId,
          }).queryKey,
          queryFn: () =>
            useGetPdpQuery.fetcher({
              organization_id: organizationId,
              portfolio_id: portfolioId,
              pdp_id: params.pdpId,
            })(),
        });

        productName = pdpData?.name ?? 'Product Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'product details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      productName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
  validateSearch: (search: Record<string, unknown>): ProductDetailsSearch => {
    const products = search.products === 'open' || search.products === 'collapsed' ? search.products : undefined;
    const tab = search.tab === 'hero' || search.tab === 'carousel' ? search.tab : undefined;
    const carouselTab =
      search.carouselTab === 'imported' || search.carouselTab === 'spark' ? search.carouselTab : undefined;
    const heroTab = search.heroTab === 'imported' || search.heroTab === 'spark' ? search.heroTab : undefined;
    const view = search.view === ViewMode.ORIGINAL ? search.view : undefined;
    return { products, tab, carouselTab, heroTab, view };
  },
});

function RouteComponent() {
  const { pdpId } = useParams({ from: Route.id });
  const search = useSearch({ from: Route.id });

  return (
    <PagesProvider template={BreadcrumbsPageTemplate}>
      <ProductDetailsPage
        pdpId={pdpId}
        drawerState={search.products}
        activeTab={search.tab}
        carouselSubTab={search.carouselTab}
        heroSubTab={search.heroTab}
        viewMode={search.view}
      />
    </PagesProvider>
  );
}
